import React from 'react';
import {createRoot} from 'react-dom/client';
import {Provider} from 'react-redux';

import getStore from './store';
import {initialAppState} from './slices/appSlice';
import {initialMatrixState, setPreloadedOptions} from './slices/matrixSlice';
import {initialSharedState} from './slices/sharedSlice';
import App from './App';
import InitialStateLoader from "./InitialStateLoader";
import {ensureSdkLoaded} from './utils/dpleSdk';

// Locked options for DPLE mode — predesign defines shape and size.
const DPLE_LOCKED_OPTIONS = ['shape', 'format'];

// Reads the per-country DPLE config exposed by Drupal in drupalSettings.dple
// (mirrored from avery_dple.settings in sites/config_overrides/<country>.php).
// Returns null when activation params are absent or when required per-country
// settings are missing — the latter fails closed rather than silently picking
// a default endpoint/target, which would render foreign-country content.
const readDpleParams = () => {
  const params = new URLSearchParams(window.location.search);
  if (params.get('mode') !== 'dple' || !params.get('predesign')) {
    return null;
  }

  // sku is mandatory: the configurator's product selection is driven by
  // ?cw=&ch=&s=, but the DPLE SDK still needs a real catalogue SKU for its
  // asset lookup. Without it the editor would receive whatever placeholder
  // M2 emits for a custom-format quote (e.g. "W30_Custom_Size-S"), which DPP
  // rejects with "predesign doesn't belong to sku".
  const sku = params.get('sku');
  if (!sku) {
    // eslint-disable-next-line no-console
    console.error('[DPLE] missing required URL param "sku". Refusing to enter DPLE mode.');
    return null;
  }

  const dpleSettings = window.drupalSettings?.dple || {};
  // URL param overrides exist only for developer testing; in production the
  // values come exclusively from the per-country config so a single visitor
  // cannot accidentally pull from another country's catalogue.
  const endpoint = params.get('endpoint') || dpleSettings.endpoint;
  const target = params.get('target') || dpleSettings.target;
  const consumer = dpleSettings.consumer;
  const sdkUrl = dpleSettings.sdk_url;

  if (!endpoint || !target || !consumer || !sdkUrl) {
    // eslint-disable-next-line no-console
    console.error(
      '[DPLE] missing required drupalSettings.dple values; expected endpoint, target, consumer, sdk_url. Refusing to enter DPLE mode.',
      dpleSettings,
    );
    return null;
  }

  // Custom label dimensions are also URL-supplied — kept here so the
  // DPLE preview can reserve an aspect-ratio box and avoid CLS while
  // the SDK is still loading. parseInt mirrors what InitialStateLoader
  // does when consuming the same params.
  const cwParam = parseInt(params.get('cw'), 10);
  const chParam = parseInt(params.get('ch'), 10);

  return {
    enabled: true,
    sku,
    predesignName: params.get('predesign'),
    endpoint,
    target,
    consumer,
    sdkUrl,
    cw: Number.isFinite(cwParam) && cwParam > 0 ? cwParam : null,
    ch: Number.isFinite(chParam) && chParam > 0 ? chParam : null,
    // Server-gated debug flag (admin + ?debug=true). React reads this off
    // the Redux config instead of re-sniffing the query param so the
    // permission check stays single-source.
    debug: dpleSettings.debug === true,
  };
};

const reactRoot = document.getElementById('configurator');
const placeholder = reactRoot.querySelector('.placeholder');

// Check for an external H1 before React renders, to avoid the render-then-
// remove CLS of drawing our own headline and then hiding it. Only a page-title
// H1 positioned ABOVE the app counts as the duplicate we defer to. H1s that
// content editors place BELOW the app (configurator tabs, FAQ, SEO copy) must
// not suppress the headline, so ignore any that don't precede the app in
// document order.
const allH1s = Array.from(document.querySelectorAll('h1'));
const hasExternalH1 = allH1s.some(h1 => !reactRoot.contains(h1)
  && (reactRoot.compareDocumentPosition(h1) & Node.DOCUMENT_POSITION_PRECEDING));

const {instances, settings, shared} = drupalSettings.avery_configurator.state;
const matrix = {...initialMatrixState, ...instances[0].state.matrix};
const attributeImageRules = shared.attribute_image_rules || [];

const dpleParams = readDpleParams();

// In DPLE mode, lock shape/format before initial state is consumed so the
// preloaded `setPreloadedOptions` dispatch picks up the right `pageOptionsHide`
// in its `variables` payload (server uses it for subsequent webProduct fetches).
if (dpleParams) {
  matrix.pageOptionsHide = [...(matrix.pageOptionsHide || []), ...DPLE_LOCKED_OPTIONS];
  // Head start on the download; the wizard awaits the same promise when it
  // mounts and is what reports a failure to the visitor. Swallow the
  // rejection here so a failed prefetch doesn't surface as an unhandled one.
  ensureSdkLoaded(dpleParams.sdkUrl).catch(() => {});
  // The configurator page is server-rendered for its default product, so
  // the SSR'd `.placeholder` already contains that product's Media tag.
  // Without intervention it flashes for a paint or two before the wizard
  // hydrates (and would render again whenever InitialStateLoader's pre-
  // `ready` branch re-emits the same HTML via dangerouslySetInnerHTML).
  // Wipe it synchronously here so the wrong artwork never gets a chance
  // to paint — the wizard's own loading state takes over instantly.
  if (placeholder) {
    placeholder.innerHTML = '';
  }
}

// Create store with initial state.
const storeInstance = getStore({
  instances,
  app: {
    ...initialAppState,
    ...instances[0].state.app,
    ...(dpleParams ? { dple: {
      enabled: true,
      sku: dpleParams.sku,
      predesignName: dpleParams.predesignName,
      endpoint: dpleParams.endpoint,
      target: dpleParams.target,
      consumer: dpleParams.consumer,
      sdkUrl: dpleParams.sdkUrl,
      cw: dpleParams.cw,
      ch: dpleParams.ch,
      debug: dpleParams.debug,
    }} : {}),
  },
  matrix,
  shared: {
    ...initialSharedState,
    ...shared,
  },
});

// Broadcast matrix.code changes so non-React listeners (e.g. the
// configurator-tabs-bridge that toggles server-rendered <ft-tabs> visibility)
// can react without coupling to the Redux store directly. Subscribe BEFORE
// the initial setPreloadedOptions dispatch so the first code is captured.
let lastBroadcastCode = null;
const broadcastCode = () => {
  const code = storeInstance.getState().matrix?.code;
  if (code && code !== lastBroadcastCode) {
    lastBroadcastCode = code;
    // Expose the latest code so listeners attached after the dispatch can
    // still sync (e.g. the configurator-tabs-bridge on Drupal.behaviors.attach).
    window.averyConfiguratorCode = code;
    document.dispatchEvent(new CustomEvent('configurator:code-changed', {detail: {code}}));
  }
};
storeInstance.subscribe(broadcastCode);

// If preloaded options exist and no URL overrides, populate the store
// BEFORE React renders. This avoids the useEffect → setState cycle in
// InitialStateLoader that causes a placeholder → App DOM swap (CLS).
//
// In DPLE mode the gallery card always emits cw/ch/s overrides, so
// hasUrlOverrides is true and we fall through to InitialStateLoader's
// fetchChangeOptions path — no DPLE-specific dispatch is needed.
const urlParams = new URLSearchParams(window.location.search);
const hasUrlOverrides = ['cq', 'cw', 'ch', 's', 'mat', 'fin'].some(p => urlParams.has(p));

if (matrix.preloadedOptions && !hasUrlOverrides) {
  storeInstance.dispatch(setPreloadedOptions({
    data: {webProduct: matrix.preloadedOptions},
    isInitialRequest: true,
    attributeImageRules,
    variables: {
      defaultConfig: matrix.defaultConfig,
      client: matrix.AveryCustom?.client,
      customVat: matrix.AveryCustom?.customVat,
      productId: matrix.productId,
      priceListStyle: matrix.priceListStyle,
      denyCustomFormats: matrix.denyCustomFormats,
      pageOptionsFilter: matrix.pageOptionsFilter,
      pageOptionsHide: matrix.pageOptionsHide,
    },
  }));
}

const placeholderHtml = placeholder.outerHTML || '<div>Loading...</div>';

const renderApp = () => {
  return (
    <Provider store={storeInstance}>
      <InitialStateLoader {...{...instances[0].state.matrix, placeholder: placeholderHtml}}>
        <App {...{settings, hasExternalH1}} />
      </InitialStateLoader>
    </Provider>);
};

const root = createRoot(reactRoot);

// Dev-only drift guard: the server placeholder and whatever React renders in
// its place must be the same height, otherwise the swap produces a layout
// shift (visible once the body-visibility anti-flicker hack is removed). With
// preloaded options this compares the placeholder against the hydrated app;
// with URL overrides React first paints its own placeholder, so it compares
// the two skeletons — both are meant to match the Twig skeleton anyway. The
// tolerance absorbs the rating widget's reflow and per-product skeleton-
// estimate variance; a genuinely missed skeleton block is far larger. When it
// warns, re-sync the Twig skeleton in avery_configurator/includes/ with
// App.jsx.
const PLACEHOLDER_DRIFT_THRESHOLD = 50;
const checkPlaceholderDrift =
  !dpleParams && window.location.hostname.endsWith('.ddev.site');
const placeholderHeight = checkPlaceholderDrift ? reactRoot.offsetHeight : 0;

root.render(renderApp());

// Catch any code already in state at this point (e.g. set during
// setPreloadedOptions before the subscriber attached).
broadcastCode();

if (checkPlaceholderDrift) {
  requestAnimationFrame(() => requestAnimationFrame(() => {
    const appHeight = reactRoot.offsetHeight;
    const delta = appHeight - placeholderHeight;
    if (Math.abs(delta) > PLACEHOLDER_DRIFT_THRESHOLD) {
      // eslint-disable-next-line no-console
      console.warn(
        `[configurator] Placeholder/app height drift ${delta > 0 ? '+' : ''}${delta}px ` +
        `(placeholder ${placeholderHeight}px, app ${appHeight}px). ` +
        'Re-sync the Twig skeleton in avery_configurator/includes/ with App.jsx.',
      );
    }
  }));
}
